import { batch, createSignal, untrack } from 'solid-js';
import { fetchBusiness, clearBusiness } from '@/shared/stores/business.store';
import {
  fetchPermissions,
  clearPermissions,
} from '@/shared/stores/permissions.store';
import { clearStorehouses } from '@/shared/stores/storehouses.store';
import { clearSubscription } from '@/features/billing/store/subscription.store';
import { clearDevContext } from '@/features/dev/lib/dev-context';
import { initLocaleForUser, clearUserLocale } from '@/shared/i18n';
import { queryCache } from '@/shared/lib/query-cache';
import type { User, SessionStatus } from '../types/auth.types';
import type {
  SignupRequest,
  CompleteRegistrationRequest,
  CompleteInvitationRequest,
} from '../types/auth.types';
import * as authApi from '../api/auth.api';
import type { AppError } from '@/shared/types/api.types';
import { normalizeError } from '@/shared/lib/errors';
import { refreshSession } from '@/shared/lib/api-client';

/**
 * Session Store
 *
 * Manages authentication state using Solid signals.
 * Features:
 * - Session initialization on app startup
 * - Login/logout actions
 * - Current user state
 * - Loading and error states
 */

// Session state
export type LogoutReason = 'session_replaced' | 'expired' | 'manual' | null;

const [status, setStatus] = createSignal<SessionStatus>('loading');
const [user, setUser] = createSignal<User | null>(null);
const [error, setError] = createSignal<AppError | null>(null);
const [isInitialized, setIsInitialized] = createSignal(false);
const [logoutReason, setLogoutReason] = createSignal<LogoutReason>(null);

// Token stored in memory (not localStorage for security)
const [accessToken, setAccessToken] = createSignal<string | null>(null);

// Dev convenience only: keep access token across hard reloads on localhost.
// Production continues using memory + HttpOnly refresh cookie only.
const DEV_ACCESS_TOKEN_KEY = 'sm_dev_access_token';

function readDevAccessToken(): string | null {
  if (!import.meta.env.DEV) return null;
  try {
    return sessionStorage.getItem(DEV_ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
}

function writeDevAccessToken(token: string | null): void {
  if (!import.meta.env.DEV) return;
  try {
    if (token) sessionStorage.setItem(DEV_ACCESS_TOKEN_KEY, token);
    else sessionStorage.removeItem(DEV_ACCESS_TOKEN_KEY);
  } catch {
    // ignore storage failures
  }
}

/**
 * Initialize session on app startup
 *
 * This will attempt to get the current user. If there's no access token in memory
 * but a valid refresh_token cookie exists, the api-client will automatically:
 * 1. Get a 401 from /auth/profile
 * 2. Call /auth/refresh with the refresh cookie
 * 3. Store the new access token in memory via updateAccessToken()
 * 4. Retry /auth/profile successfully
 */
export async function initSession(): Promise<void> {
  if (isInitialized()) {
    return;
  }

  setStatus('loading');
  setError(null);

  try {
    // After a hard refresh, access token memory is empty. Try refresh first
    // so we avoid an expected /auth/profile 401 on every page load.
    if (!getAccessToken()) {
      await refreshSession();
    }

    // With a fresh (or existing) access token, fetch current user.
    const currentUser = await authApi.getCurrentUser();
    setUser(currentUser);
    setStatus('authenticated');
    // Apply the user's locale — DB value takes priority over localStorage
    initLocaleForUser(currentUser._id, currentUser.locale);
    // Load business data and permissions into shared stores
    fetchBusiness();
    fetchPermissions();
  } catch {
    // Only truly unauthenticated if refresh failed or no refresh cookie
    batch(() => {
      setUser(null);
      setStatus('unauthenticated');
    });
    // Don't set error for initial load failures (user just not logged in)
  } finally {
    setIsInitialized(true);
  }
}

/**
 * Login with credentials
 */
export async function loginUser(
  email: string,
  password: string
): Promise<{ success: boolean; error?: AppError }> {
  setError(null);

  try {
    const response = await authApi.login({ email, password });
    // Extract user and store token in memory
    batch(() => {
      setUser(response.user);
      updateAccessToken(response.accessToken);
      setStatus('authenticated');
      setLogoutReason(null); // clear any stale reason from previous session
    });
    // Apply the user's saved locale preference — DB value takes priority
    initLocaleForUser(response.user._id, response.user.locale);
    // Load business data and permissions into shared stores
    fetchBusiness(true);
    fetchPermissions(true);
    return { success: true };
  } catch (err) {
    const appError = normalizeError(err);
    setError(appError);
    return { success: false, error: appError };
  }
}

/**
 * Logout current user
 */
export async function logoutUser(): Promise<void> {
  // Set the guard BEFORE the API call so any in-flight request that gets a
  // 401 "session replaced" response is recognized as an expected consequence
  // of THIS logout, not a real concurrent login from another device.
  _loggingOut = true;
  try {
    await authApi.logout();
  } catch {
    // Logout locally even if API call fails
  } finally {
    // Cancel any pending proactive refresh timer from the old session
    if (_refreshTimer) {
      clearTimeout(_refreshTimer);
      _refreshTimer = null;
    }
    // batch: same reason as clearSessionLocally — flush all signal mutations
    // atomically so SolidJS renders exactly one transition to unauthenticated.
    batch(() => {
      setUser(null);
      setAccessToken(null);
      setStatus('unauthenticated');
      setError(null);
      setLogoutReason('manual');
    });
    clearBusiness();
    clearPermissions();
    clearStorehouses();
    clearSubscription();
    clearDevContext();
    // Disassociate locale changes from the logged-out user
    clearUserLocale();
    // Evict all cached API responses so the next session starts clean
    queryCache.clear();
    _loggingOut = false;
  }
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return status() === 'authenticated' && user() !== null;
}

/**
 * Check if session is still loading
 */
export function isLoading(): boolean {
  return status() === 'loading';
}

/**
 * Get current user
 */
export function getUser(): User | null {
  return user();
}

/**
 * Get current error
 */
export function getError(): AppError | null {
  return error();
}

/**
 * Get session status
 */
export function getStatus(): SessionStatus {
  return status();
}

/**
 * Check if current user has the dev role
 */
export function isDev(): boolean {
  return getUser()?.appRole === 'dev';
}

/**
 * Export signals for reactive components
 */
export const session = {
  status,
  user,
  error,
  isInitialized,
  logoutReason,
};

// Export individual signals for convenience
export { status, user, error, isInitialized, logoutReason };

/**
 * Clear the logout reason (call after displaying the message to the user)
 */
export function clearLogoutReason(): void {
  setLogoutReason(null);
}

/**
 * Get current access token from memory
 */
export function getAccessToken(): string | null {
  return accessToken() ?? readDevAccessToken();
}

/**
 * Update access token (used by refresh flow).
 * Also reschedules the proactive refresh timer for the new token.
 */
export function updateAccessToken(token: string): void {
  setAccessToken(token);
  writeDevAccessToken(token);
  scheduleTokenRefresh(token);
}

// Timer handle for proactive token refresh
let _refreshTimer: ReturnType<typeof setTimeout> | null = null;

// True while a manual logout is in progress. Used by api-client to suppress
// false "session replaced by new login" detection on requests that race the
// logout API call (the backend invalidates the session, so any in-flight
// request sees rcode 4105 even though the user just clicked logout themselves).
let _loggingOut = false;
export function isLoggingOut(): boolean {
  return _loggingOut;
}

/**
 * Schedule a proactive token refresh 60 seconds before the access token expires.
 * This prevents the 401 → refresh → retry cycle during active sessions.
 * Safe to call multiple times — cancels any existing timer first.
 */
export function scheduleTokenRefresh(token: string): void {
  if (_refreshTimer) {
    clearTimeout(_refreshTimer);
    _refreshTimer = null;
  }

  try {
    // Decode JWT payload (base64url, middle segment) — no secret needed client-side
    const payload = JSON.parse(
      atob(token.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/'))
    );
    const expiresAt = payload.exp * 1000; // exp is in seconds
    const refreshAt = expiresAt - 60_000; // refresh 60s before expiry
    const delay = refreshAt - Date.now();

    if (delay <= 0) return; // already expiring — let the 401 path handle it

    _refreshTimer = setTimeout(async () => {
      // Don't refresh proactively when the page is hidden — this would keep
      // the server-side idle timeout from ever firing on a neglected desktop tab.
      // The visibilitychange handler below triggers a catch-up refresh on resume.
      if (document.hidden) return;

      // Only refresh if still authenticated (user may have logged out in the meantime).
      // untrack: reading a signal inside a setTimeout is outside any reactive root;
      // wrapping with untrack() prevents the "computation created outside createRoot" warning.
      if (untrack(status) !== 'authenticated') return;
      try {
        await refreshSession();
      } catch {
        // Silently ignore — if the refresh token has genuinely expired the next
        // API call will return 401, handleUnauthorized will try again, and if
        // that also fails clearSessionLocally() will redirect to login.
      }
    }, delay);
  } catch {
    // Malformed token — ignore, let the 401 path handle it
  }
}

/**
 * Clear all local session state without calling the API.
 * Used by the api-client when a token refresh fails (session expired),
 * so ProtectedRoute reactively redirects to /login.
 *
 * @param reason - Why the session was cleared. 'session_replaced' means
 *   another device/browser logged in and this session was evicted.
 */
export function clearSessionLocally(reason: LogoutReason = 'expired'): void {
  if (_refreshTimer) {
    clearTimeout(_refreshTimer);
    _refreshTimer = null;
  }
  // batch: flush all signal mutations as a single SolidJS update so components
  // see a consistent state and never render an intermediate "no user but still
  // authenticated" snapshot that triggers the insertBefore reconciliation panic.
  batch(() => {
    setLogoutReason(reason);
    setUser(null);
    setAccessToken(null);
    setStatus('unauthenticated');
    setError(null);
  });
  writeDevAccessToken(null);
  clearBusiness();
  clearPermissions();
  clearStorehouses();
  clearSubscription();
  clearDevContext();
  clearUserLocale();
  // Evict all cached API responses so the next session starts clean
  queryCache.clear();
}

/**
 * When the browser tab becomes visible again (user switches back to the app),
 * check if the access token has expired or is about to expire while the tab was
 * hidden, and refresh immediately if so.
 *
 * This complements the `document.hidden` guard in scheduleTokenRefresh: together
 * they ensure the server-side 24 h idle timeout applies consistently to all
 * clients — the proactive timer only runs while the user is actively on the page.
 */
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    // untrack: this handler runs at module scope, outside any SolidJS reactive
    // root. Reading status() here without untrack() creates an orphaned
    // computation that triggers the "created outside createRoot" warning and
    // can cause insertBefore panics when signals fire during DOM reconciliation.
    if (document.hidden || untrack(status) !== 'authenticated') return;

    const token = getAccessToken();
    if (!token) return;

    try {
      const payload = JSON.parse(
        atob(token.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/'))
      );
      const expiresAt = (payload.exp as number) * 1000;
      // Refresh if the token has expired or is within the 60-second window
      if (Date.now() > expiresAt - 60_000) {
        refreshSession().catch(() => {
          // Silently swallow — if the refresh token is expired the next API call
          // returns 401, handleUnauthorized triggers another attempt, and if that
          // also fails clearSessionLocally() redirects to login.
        });
      }
    } catch {
      // Malformed token — let the 401 path handle it
    }
  });
}

/**
 * Sign up with email only (Step 1)
 * Sends verification email — does NOT log the user in
 */
export async function signupUser(
  data: SignupRequest
): Promise<{ success: boolean; error?: AppError }> {
  setError(null);

  try {
    await authApi.signup(data);
    return { success: true };
  } catch (err) {
    const appError = normalizeError(err);
    setError(appError);
    return { success: false, error: appError };
  }
}

/**
 * Complete registration (Step 2 — onboarding wizard)
 * Creates user + business + storehouse and logs them in
 */
export async function completeRegistrationUser(
  data: CompleteRegistrationRequest
): Promise<{ success: boolean; error?: AppError }> {
  setError(null);

  try {
    const response = await authApi.completeRegistration(data);
    batch(() => {
      setUser(response.user);
      updateAccessToken(response.accessToken);
      setStatus('authenticated');
    });
    // Apply the user's locale (first time — keeps current locale)
    initLocaleForUser(response.user._id, response.user.locale);
    // Load business data and permissions into shared stores
    fetchBusiness(true);
    fetchPermissions(true);
    return { success: true };
  } catch (err) {
    const appError = normalizeError(err);
    setError(appError);
    return { success: false, error: appError };
  }
}

/**
 * Complete invitation (invited users)
 * Joins existing business and logs them in
 */
export async function completeInvitationUser(
  data: CompleteInvitationRequest
): Promise<{ success: boolean; error?: AppError }> {
  setError(null);

  try {
    const response = await authApi.completeInvitation(data);
    batch(() => {
      setUser(response.user);
      updateAccessToken(response.accessToken);
      setStatus('authenticated');
    });
    // Apply the user's locale (first time — will keep current locale)
    initLocaleForUser(response.user._id, response.user.locale);
    // Load business data and permissions into shared stores
    fetchBusiness(true);
    fetchPermissions(true);
    return { success: true };
  } catch (err) {
    const appError = normalizeError(err);
    setError(appError);
    return { success: false, error: appError };
  }
}

/**
 * Export setters for direct updates (used in signup flow)
 */
export { setUser, setStatus, setError };
